import React from 'react';
import { useSelector } from 'react-redux';
import { Forms, Loading, SystemText } from 'e4shared';
import Filters from '../_filters';
import Chart from './chart';
import BarChart from './barChart';
import GroupPicker from 'components/admin/forms/groupPicker';
import MetricPicker from 'components/admin/forms/metricPicker';

import WidgetBase, { competencySet, groupSet, metricSet, widgetLoad, widgetLoadRecommendations } from '../_base';
import CompetencyRecommendations from '../components/competencyRecommendations';
import Correlation from '../components/correlation';
import CompetencyPicker from 'components/admin/forms/competencyPicker';

export const SalesVelocityHelp = () => {
    const st = prop => SystemText(`Dashboard/Widgets/SalesVelocity/Help/${prop}`);

    return (
        <div>
            <p>
                <b>{st('Title')}</b> {st('Summary')}
            </p>
            <label>{st('Opportunities')}</label>
            <p>{st('Opportunities/Summary')}</p>
            <label>{st('Size')}</label>
            <p>{st('Size/Summary')}</p>
            <label>{st('Rate')}</label>
            <p>{st('Rate/Summary')}</p>
            <label>{st('Length')}</label>
            <p>{st('Length/Summary')}</p>
            <div className="text-center text-lg mt10">
                <div className="flex flex-center">
                    <div>{st('Equation/Velocity')} =</div>
                    <div className="pl10">
                        <div>{st('Equation/SizexRate')}</div>
                        <hr style={{ borderWidth: '2px' }} />
                        <div>{st('Equation/Length')}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const SalesVelocity = ({ defaultProps, id, item, setItem, onDelete, history }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);

    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [initialised, setInitialised] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [recLoading, setRecLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [recommendationData, setRecommendationData] = React.useState({});
    const [flipped, setFlipped] = React.useState(true);

    React.useEffect(() => {
        if (item) {
            const parsed = JSON.parse(item.data);
            setValues({ ...defaults, ...parsed });
            setInitialised(true);
        }
    }, [item]);

    React.useEffect(() => {
        initialised && load();
    }, [initialised]);

    React.useEffect(() => {
        if (initialised) {
            const temp = { ...values };
            if (values.type1 !== 'C') temp.competency1 = null;
            if (values.type1 !== 'M') temp.metric1 = null;
            setValues(temp);
        }
    }, [initialised, values?.type1]);

    const load = () => {
        widgetLoad(item.id, user.guid, validator, setLoading, setNotConfigured, setData, () => {
            widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData);
        });
    };

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;
        setValues(temp);
    };

    const height = defaultProps.height(item);

    const defaults = { show_For: true, period: 'M', type1: '' };

    const validator = suppressErrors => {
        const form = values || JSON.parse(item.data);
        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Edit/Fields/Name/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = SystemText('Dashboard/Widgets/SalesVelocity/Fields/Group/Error');
            validForm = false;
        }

        if (form.metric1?.subMetricId && (!form.metric1?.subMetricValues || form.metric1?.subMetricValues.length === 0)) {
            errors.metric1 = SystemText('Dashboard/Widgets/SalesVelocity/Fields/Metric/Error');
            validForm = false;
        }

        if (form.type1 === 'C' && !competencySet(form.competency1)) {
            errors.competency1 = SystemText('Dashboard/Widgets/SalesVelocity/Fields/Competency/Error');
            validForm = false;
        }

        if (form.type1 === 'M' && !metricSet(form.metric1)) {
            errors.metric1 = SystemText('Dashboard/Widgets/SalesVelocity/Fields/Metric/Error');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const chart = (
        <div
            className={`chart-container ${flipped ? 'flipped' : ''}`}
            id={`chart-${item.id}`}
            style={{ height: `${height}px` }}
            onClick={() => setFlipped(!flipped)}
        >
            <div className="chart" style={{ height: `${height}px` }}>
                <Chart front={flipped} data={[data[0]]} hasBars={data.length > 1} item={item} id={`chart-${item.id}`} />
            </div>
            {data.length > 1 && (
                <div className="chart" style={{ height: `${height}px` }}>
                    <BarChart front={!flipped} data={[data[1].data]} item={item} id={`chart-${item.id}`} axis="right" />
                </div>
            )}
        </div>
    );

    const recommendations = () => {
        if (recLoading) return <Loading />;

        const result = [];

        recommendationData?.correlation && result.push(<Correlation widget={item} value={recommendationData.correlation} />);

        recommendationData?.comps &&
            result.push(
                <CompetencyRecommendations
                    text={SystemText('Dashboard/Widgets/SalesVelocity/Recommends/Strongest')}
                    comps={recommendationData?.comps}
                />
            );

        return result;
    };

    const filters = (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <GroupPicker
                mandatory
                allowMultiple
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                showUserOption={defaultProps.company}
                controlId={id}
            />

            <Forms.Buttons label={SystemText('Dashboard/Widgets/SalesVelocity/Fields/Extra/Label')}>
                <button
                    className={`btn ${values.type1 === '' ? 'btn-primary' : ''}`}
                    onClick={() => updateProperty('', 'type1')}
                >
                    {SystemText('Dashboard/Widgets/SalesVelocity/Fields/Extra/Toggle/None')}
                </button>
                <button
                    className={`btn ${values.type1 === 'C' ? 'btn-primary' : ''}`}
                    onClick={() => updateProperty('C', 'type1')}
                >
                    {SystemText('Dashboard/Widgets/SalesVelocity/Fields/Extra/Toggle/Competency')}
                </button>
                <button
                    className={`btn ${values.type1 === 'M' ? 'btn-primary' : ''}`}
                    onClick={() => updateProperty('M', 'type1')}
                >
                    {SystemText('Dashboard/Widgets/SalesVelocity/Fields/Extra/Toggle/Metric')}
                </button>
            </Forms.Buttons>
            {values.type1 === 'C' && (
                <>
                    <CompetencyPicker
                        label={SystemText('Dashboard/Widgets/SalesVelocity/Fields/Competency/Label')}
                        mandatory
                        value={values.competency1}
                        error={errors.competency1}
                        onChange={value => updateProperty(value, 'competency1')}
                    />
                </>
            )}
            {values.type1 === 'M' && (
                <MetricPicker
                    label={SystemText('Dashboard/Widgets/SalesVelocity/Fields/Metric/Label')}
                    placeholder={SystemText('Dashboard/Widgets/SalesVelocity/Fields/Metric/Placeholder')}
                    value={values.metric1}
                    error={errors.metric1}
                    onChange={value => updateProperty(value, 'metric1')}
                    showAverage
                />
            )}
        </Filters>
    );
    return (
        <WidgetBase
            filters={filters}
            chart={chart}
            height={height}
            item={item}
            recommendations={recommendations()}
            id={id}
            help={<SalesVelocityHelp />}
            hideConfig={defaultProps.company && !user.administrator}
            extra={null}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default SalesVelocity;
